import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useStore from "../../../useStore";
import Loader from "../../Elements/Loader/Loader";
import "./detalleEmpleado.css";
import Button from "../../Elements/Button/Button";
import CalendarioEmpleado from "./CalendarioEmpleado/CalendarioEmpleado";
import DetalleEmpleadoHistorico from "./DetalleEmpleadoHistorico/DetalleEmpleadoHistorico";
import DetalleEmpleadoVacaciones from "./DetalleEmpleadoVacaciones/DetalleEmpleadoVacaciones";
import DetalleEmpleadoBank from "./DetalleEmpleadoBank/DetalleEmpleadoBank";
import DetalleEmpleadoContacto from "./DetalleEmpleadoContacto/DetalleEmpleadoContacto";
import DetalleEmpleadoDatos from "./DetalleEmpleadoDatos/DetalleEmpleadoDatos";
import { useAuthentication } from "../../../Hooks/useAuthentication";

function getAvatar(nombre, apellidoPaterno) {
  const ini = (nombre?.[0] || "") + (apellidoPaterno?.[0] || "");
  return ini.toUpperCase();
}

function getTiempoTrabajado(fechaIngreso) {
  if (!fechaIngreso) return "-";
  const fechaI = new Date(fechaIngreso);
  const hoy = new Date();
  let años = hoy.getFullYear() - fechaI.getFullYear();
  let meses = hoy.getMonth() - fechaI.getMonth();
  if (meses < 0) {
    años -= 1;
    meses += 12;
  }
  if (años < 0) return "-";
  if (años === 0 && meses === 0) return "Menos de 1 mes";
  let texto = "";
  if (años > 0) texto += `${años} año${años > 1 ? "s" : ""}`;
  if (meses > 0) texto += `${años > 0 ? " y " : ""}${meses} mes${meses > 1 ? "es" : ""}`;
  return texto;
}

export default function DetalleEmpleadoPage() {
  const { idEmpleado } = useParams();
  const navigate = useNavigate();

  const {
    fetchDetalleEmpleado,
    detalleEmpleado,
    loadingDetalleEmpleado,
    errorDetalleEmpleado,
    fetchChecador,
    checador,
    loadingChecador
  } = useStore();

  const loginData = JSON.parse(localStorage.getItem("logindata") || "{}");
  const token = loginData?.data?.token;
  const { role } = useAuthentication();
  const isAdmin = role === "admin";

  useEffect(() => {
    if (idEmpleado && token) {
      (async () => {
        try {
          await fetchDetalleEmpleado(idEmpleado, token);
        } catch (err) {
          console.error("Error al buscar detalle de empleado:", err);
        }
      })();
    }
  }, [idEmpleado, token, fetchDetalleEmpleado]);

  useEffect(() => {
    (async () => {
      try {
        await fetchChecador();
      } catch (err) {
        console.error("Error al cargar checadas:", err);
      }
    })();
  }, [fetchChecador]);

  // --- Desestructurar datos del detalle ---
  const datosPersonales = detalleEmpleado?.datosPersonales || {};
  const datosPuesto = detalleEmpleado?.datosPuesto || {};
  const datosBancarios = detalleEmpleado?.datosBancarios || {};
  const numeroEmpleadoEmpleado = detalleEmpleado?.numeroEmpleado ?? "-";
  const tieneContrato = !!detalleEmpleado?.contrato?.key;
  const contratoKey = detalleEmpleado?.contrato?.key;
  const contratoBucket = detalleEmpleado?.contrato?.bucket || "recursos-humanos-qs";
  const region = "us-east-2"; // ajusta si cambias región

  const verContratoPublico = () => {
    const url = `https://${contratoBucket}.s3.${region}.amazonaws.com/${encodeURI(contratoKey)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const {
    nombre,
    apellidoPaterno,
    apellidoMaterno,
    curp: curpEmpleado,
    rfc,
    fechaNacimiento,
    numeroSeguroSocial,
    gradoEstudios,
  } = datosPersonales || {};

  const {
    area,
    puesto,
    periodos = [],
    incapacidades = [],
    permisos = [],
    faltas = []
  } = datosPuesto;

  const ultimoPeriodo = periodos.length > 0 ? periodos[periodos.length - 1] : null;
  const activo = ultimoPeriodo?.activo === true;
  const fechaIngresoActual = ultimoPeriodo?.fechaIngreso || null;
  const fechaEgresoActual = ultimoPeriodo?.fechaEgreso || null;

  const checadasEmpleado = (checador || []).filter(
    (item) => String(item?.parsed?.employee_number).trim() === String(detalleEmpleado?.numeroEmpleado).trim()
  );

  if (loadingDetalleEmpleado || loadingChecador) return <Loader />;
  if (errorDetalleEmpleado)
    return (
      <div className="container py-5 container-mh">
        <div className="alert alert-danger text-center">
          {errorDetalleEmpleado.message || "Error al cargar el detalle"}
        </div>
        <Button
          text="Regresar"
          action={() => navigate(-1)}
          color="gray"
          fill
          classNames="mt-3"
        />
      </div>
    );
  if (!detalleEmpleado)
    return (
      <div className="container py-5 container-mh">
        <div className="alert alert-warning text-center">No se encontró el empleado.</div>
        <Button
          text="Regresar"
          action={() => navigate(-1)}
          color="gray"
          fill={false}
          classNames="mt-3"
        />
      </div>
    );

  return (
    <div className="container py-4 container-mh">
      <div className="container-detalle-empleado shadow-sm">
        {/* Header */}
        <div className="row align-items-center border-bottom pb-3 mb-4">
          <div className="col-auto">
            <div className="avatar-lg bg-primary text-white">
              {getAvatar(nombre, apellidoPaterno)}
            </div>
          </div>
          <div className="col">
            <h3 className="mb-1">{`${nombre} ${apellidoPaterno} ${apellidoMaterno}`}</h3>
            <span className="badge bg-light text-dark me-2">{puesto}</span>
            <span className="badge bg-secondary">{area}</span>
          </div>
          <div className="col-auto">
            <Button text="← Regresar" action={() => navigate(-1)} color="green" fill classNames="me-2" />
            {tieneContrato && (
              <Button
                text="Ver contrato"
                color="purple"
                fill
                action={verContratoPublico}
              />
            )}
            {isAdmin && (
              <Button
                text="Editar"
                color="blue"
                fill
                action={() => {
                  window.location.href = `/updateEmpleado/${detalleEmpleado?.idEmpleado}`;
                }}
              />
            )}
          </div>
        </div>
        <DetalleEmpleadoDatos
          numeroEmpleado={numeroEmpleadoEmpleado}
          curp={curpEmpleado}
          rfc={rfc}
          fechaNacimiento={fechaNacimiento}
          numeroSeguroSocial={numeroSeguroSocial}
          gradoEstudios={gradoEstudios}
          ingresoActual={fechaIngresoActual}
          egresoActual={fechaEgresoActual}
          tiempoTrabajado={getTiempoTrabajado(fechaIngresoActual)}
          activo={activo}
        />

        <DetalleEmpleadoHistorico periodos={periodos} />
        <DetalleEmpleadoVacaciones vacacionesTomadas={datosPuesto.vacacionesTomadas} />
        <DetalleEmpleadoBank datosBancarios={datosBancarios} />
        <DetalleEmpleadoContacto contactosEmergencia={datosPersonales.contactosEmergencia} />
        <CalendarioEmpleado
          permisos={permisos}
          incapacidades={incapacidades}
          faltas={faltas}
          vacaciones={datosPuesto.vacacionesTomadas}
          checadas={checadasEmpleado}
        />
      </div>
    </div>
  );
}
